import type { FocusableElement } from "@react-types/shared";
import type { RefObject } from "react";
import { mergeProps, useTooltip as useAriaTooltip, useTooltipTrigger } from "react-aria";
import { type TooltipTriggerState, useTooltipTriggerState } from "react-stately";

import type { TooltipOptions } from "../types/overlay.d.ts";
import { useAnimeState } from "./useAnimeState.ts";
import { useOverlayWithPosition } from "./useOverlayWithPosition.ts";

interface UseTooltipOptions extends TooltipOptions {
  noPointerClickEvents?: boolean;
}

/**
 * Animation and Offset are not configurable to keep think simple
 * @deprecated
 */
export const DEPRECATED_useTooltip = (
  triggerRef: RefObject<FocusableElement | null>,
  tooltipRef: RefObject<HTMLElement | null>,
  options?: UseTooltipOptions,
) => {
  const state = useTooltipTriggerState(options);
  const { triggerProps: innerTriggerProps, tooltipProps: innerTooltipProps } = useTooltipTrigger(
    options ?? {},
    state,
    triggerRef,
  );
  const { tooltipProps } = useAriaTooltip(innerTooltipProps, state);
  const { overlayProps, arrowProps, placement, updatePosition } = useOverlayWithPosition(
    triggerRef,
    tooltipRef,
    state,
    {
      ...options,
      placement: options?.placement ?? "top",
      // if isDismissable is true, then the click event will be caught and stopped.
      isDismissable: options?.isDismissable ?? false,
      offset: options?.offset ?? 3,
    },
  );
  const animeState = useAnimeState(tooltipRef, state, placement!);

  let triggerProps = innerTriggerProps;
  if (options?.noPointerClickEvents) {
    const {
      onClick: _a,
      // @ts-expect-error to check later
      onPress: _f,
      onMouseDown: _b,
      onPointerDown: _c,
      onMouseUp: _d,
      onPointerUp: _e,
      ...cleanedTriggerProps
    } = innerTriggerProps;
    triggerProps = cleanedTriggerProps;
  }

  return {
    triggerProps,
    tooltipProps: mergeProps(tooltipProps, overlayProps),
    arrowProps,
    state: animeState as TooltipTriggerState,
    updatePosition,
  };
};
